import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Utensils, Truck, ShoppingBag, Leaf, Sparkles, Heart } from 'lucide-react';
import Divider from '../components/Divider';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_butt-karahi-luton/artifacts/dwb7qv2j_kqaK9%20%281%29.jpg";

const Home = () => {
  return (
    <main data-testid="home-page" className="bg-white">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center bg-gray-100"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_butt-karahi-luton/artifacts/46hnjilc_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-20">
          <h1 
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 animate-fade-in-up"
            data-testid="hero-title"
          >
            BUTT KARAHI
          </h1>
          <p className="text-2xl sm:text-3xl text-brand-red font-bold tracking-widest mb-6 animate-fade-in-up delay-100">
            LUTON
          </p>
          
          <p 
            className="text-xl sm:text-2xl text-white font-serif italic mb-8 animate-fade-in-up delay-200"
            data-testid="hero-tagline"
          >
            Where Every Bite Tells The Story Of Excellence!
          </p>
          
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-12 animate-fade-in-up delay-300">
            Butt Karahi Luton brings the authentic taste of traditional Pakistani cuisine to the heart of Luton. 
            Scrumptious, hygienic and nutritious food packed with layers of exotic flavour.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-400">
            <Link 
              to="/menu"
              data-testid="hero-menu-btn"
              className="bg-brand-red text-white px-10 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              View Our Menu
            </Link>
            <a 
              href="tel:01582517786"
              data-testid="hero-call-btn"
              className="bg-white text-black px-10 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-gray-100 transition-all duration-300"
            >
              Call to Order
            </a>
          </div>
        </div>
      </section>

      {/* Info Bar */}
      <section className="bg-brand-red py-8" data-testid="info-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center justify-center gap-3 text-center md:text-left">
              <Clock className="text-white" size={24} />
              <div>
                <p className="text-white/80 text-xs uppercase tracking-wider">Opening Hours</p>
                <p className="text-white font-semibold">1PM - 11:30PM Daily</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-center md:text-left">
              <MapPin className="text-white" size={24} />
              <div>
                <p className="text-white/80 text-xs uppercase tracking-wider">Address</p>
                <p className="text-white font-semibold">4 Cumberland St, Luton LU1 3BW</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-center md:text-left">
              <Phone className="text-white" size={24} />
              <div>
                <p className="text-white/80 text-xs uppercase tracking-wider">Contact</p>
                <a href="tel:01582517786" className="text-white font-semibold hover:text-white/80 transition-colors">01582 517786</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Quality Pillars */}
      <section className="py-16 px-6 bg-white" data-testid="quality-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-center text-black mb-4">
            OUR <span className="text-brand-red">QUALITY</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
            We promise to serve best quality food at our restaurant. Once people have quality food, 
            they tell others and never turn back.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Tasty Food */}
            <div 
              className="bg-gray-50 border border-gray-200 p-10 text-center hover:border-brand-red hover:shadow-lg transition-all duration-300 group"
              data-testid="pillar-tasty"
            >
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-brand-red/30 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-colors">
                <Sparkles className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Tasty Food</h3>
              <p className="text-gray-600 text-sm">
                Browse our menu for more information on our BBQ platter, karahi specialties, and refreshing drinks.
              </p>
              <Link to="/menu" className="inline-block mt-6 text-brand-red text-sm uppercase tracking-wider hover:text-red-700 transition-colors">
                View Menu →
              </Link>
            </div>

            {/* Fresh Food */}
            <div 
              className="bg-gray-50 border border-gray-200 p-10 text-center hover:border-brand-red hover:shadow-lg transition-all duration-300 group"
              data-testid="pillar-fresh"
            >
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-brand-red/30 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-colors">
                <Leaf className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Fresh Food</h3>
              <p className="text-gray-600 text-sm">
                We care about our customers and serve them the freshest, most delicious food every time.
              </p>
              <Link to="/about" className="inline-block mt-6 text-brand-red text-sm uppercase tracking-wider hover:text-red-700 transition-colors">
                Learn More →
              </Link>
            </div>

            {/* Nutritious Food */}
            <div 
              className="bg-gray-50 border border-gray-200 p-10 text-center hover:border-brand-red hover:shadow-lg transition-all duration-300 group"
              data-testid="pillar-nutritious"
            >
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center border-2 border-brand-red/30 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-colors">
                <Heart className="text-brand-red" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Nutritious Food</h3>
              <p className="text-gray-600 text-sm">
                Our food is full of nutrients, healthy, and cooked carefully following traditional methods.
              </p>
              <Link to="/about" className="inline-block mt-6 text-brand-red text-sm uppercase tracking-wider hover:text-red-700 transition-colors">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Gallery Section */}
      <section className="py-16 px-6 bg-gray-50" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-center text-black mb-4">
            SAVOUR THE <span className="text-brand-red">FLAVOURS</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
            Traditional, authentic recipes with a contemporary twist — each dish luscious and irresistible.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square overflow-hidden">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-6.jpg" 
                alt="Butt Karahi signature dish"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-10.jpg" 
                alt="Fresh naan and curries"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-7.jpg" 
                alt="Appetizers and kebabs"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="https://buttkarahisouthall.co.uk/wp-content/uploads/2023/12/JAVSAN-9.jpg" 
                alt="Traditional plating"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Services Section */}
      <section className="py-16 px-6 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-center text-black mb-4">
            ENJOY EVERY <span className="text-brand-red">MOMENT</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
            Once people get connected to real food, they never change back. We provide the best service options for your convenience.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Dine In */}
            <div className="bg-white border-2 border-gray-200 p-10 text-center hover:border-brand-red transition-all duration-300 group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red transition-colors">
                <Utensils className="text-brand-red group-hover:text-white transition-colors" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Dine-In</h3>
              <p className="text-gray-600 text-sm">
                Experience our tranquil and serene environment. The perfect setting for an unforgettable meal.
              </p>
            </div>

            {/* Delivery */}
            <div className="bg-white border-2 border-gray-200 p-10 text-center hover:border-brand-red transition-all duration-300 group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red transition-colors">
                <Truck className="text-brand-red group-hover:text-white transition-colors" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Delivery</h3>
              <p className="text-gray-600 text-sm">
                Enjoy our delicious Pakistani cuisine in the comfort of your own home. Fresh and fast delivery.
              </p>
            </div>

            {/* Takeaway */}
            <div className="bg-white border-2 border-gray-200 p-10 text-center hover:border-brand-red transition-all duration-300 group">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red transition-colors">
                <ShoppingBag className="text-brand-red group-hover:text-white transition-colors" size={36} />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-black mb-4">Takeaway</h3>
              <p className="text-gray-600 text-sm">
                Order ahead and pick up your favorites. Quick and convenient for busy schedules.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/menu"
              className="inline-block bg-brand-red text-white px-10 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
              data-testid="services-menu-btn"
            >
              View Our Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Opening Hours Banner */}
      <section 
        className="py-24 px-6 relative bg-black"
        data-testid="hours-banner"
      >
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            BEST <span className="text-brand-red">HOURS</span>
          </h2>
          <p className="text-white/60 uppercase tracking-widest text-sm mb-4">Opening Hours</p>
          <p className="text-5xl md:text-6xl text-brand-red font-bold mb-8 tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>1PM - 11:30PM</p>
          <p className="text-white/70 mb-8">Open Daily</p>
          <Link 
            to="/about"
            className="inline-block border-2 border-brand-red text-brand-red px-10 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-brand-red hover:text-white transition-all duration-300"
          >
            More About Us
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;
